import axios from "axios";

const TIMEOUT_MS = 15_000;
const MAX_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 500;
// Connection failures (ERR_NETWORK) never reach the server, so retrying is
// always safe — give them more attempts and longer backoff to ride out brief
// outages or flaky mobile connections.
const NETWORK_ERROR_MAX_RETRIES = 4;
const NETWORK_ERROR_BASE_DELAY_MS = 1_000;

const api = axios.create({ timeout: TIMEOUT_MS });
// Admin requests need more headroom than user-facing ones; pair with the
// backend admin client's 55s CSOT so the server aborts before this trips.
const apiAdmin = axios.create({ timeout: 60_000 });

function isNetworkError(error) {
  return !error.response && error.code === "ERR_NETWORK";
}

function isRetryable(error) {
  // No response means a network error, timeout, or aborted connection.
  // Connection errors mean the request never reached the server; timeouts
  // are ambiguous (the server may still be processing), so callers that
  // must avoid duplicate side effects should opt out via `retry: false`.
  if (!error.response) {
    return error.code !== "ERR_CANCELED";
  }
  const status = error.response.status;
  return status === 408 || status === 429 || (status >= 500 && status <= 599);
}

function attachRetry(client) {
  client.interceptors.response.use(undefined, async (error) => {
    const config = error.config;
    if (!config) return Promise.reject(error);

    config.__retryCount = config.__retryCount || 0;
    // `retry: false` opts out of retrying HTTP error responses and timeouts,
    // but genuine connection errors (no response, not a timeout) are still
    // retried since the request never reached the server.
    const optedOut =
      config.retry === false &&
      (error.response || error.code === "ECONNABORTED");
    if (optedOut || !isRetryable(error)) {
      return Promise.reject(error);
    }

    const network = isNetworkError(error);
    const maxRetries = network ? NETWORK_ERROR_MAX_RETRIES : MAX_RETRIES;
    const baseDelay = network ? NETWORK_ERROR_BASE_DELAY_MS : RETRY_BASE_DELAY_MS;
    if (config.__retryCount >= maxRetries) {
      return Promise.reject(error);
    }

    config.__retryCount += 1;
    const delay = baseDelay * 2 ** (config.__retryCount - 1);
    await new Promise((resolve) => setTimeout(resolve, delay));
    return client(config);
  });
}
attachRetry(api);
attachRetry(apiAdmin);

export { api, apiAdmin };
